import React from 'react';
import TabOfDataTable from '../../../Elements/DataTable/TabOfDataTable';
import ErrorMessage from '../../../Elements/ErrorMessage';
import FigmaButton from '../../../Elements/FigmaButton';
import OverWindow from '../../../Elements/TabList/OverWindow';
import ButtonColorsEnum from '../../../Enums/ButtonColorsEnum';
import ButtonTypeEnum from '../../../Enums/ButtonTypeEnum';
import IconPositionEnum from '../../../Enums/IconPositionEnum';
import ChecklistCopySourceItem from '../../../Domain/ChecklistCopySourceItem';
import OpportunitySecurityChecklistService from '../../../Services/opportunitySecurityChecklistService';
import ChecklistCopySourceSearchParams from './ChecklistCopySourceSearchParams';

// Step 1 of Copy Checklist from Another Opportunity (CD-5407): pick the Opportunity to copy from, then read its
// checklist. Deliberately the same shape as ChooseServicePanel ("Copy Service From"): an OverWindow holding a
// searchable, single-select data table and one footer button that hands the chosen item to the caller, which
// then opens the ordinary edit wizard pre-filled with it.
//
// The rows are whatever the server offers as eligible (this client's won Opportunities with a completed
// checklist): the rule lives in the microservice operation behind GetCopySources, not here.
export default class ChooseChecklistSourcePanel extends React.Component {
  static defaultProps = {
    opportunityId: null,
    clickOnCancel: () => {},
    clickOnSubmit: () => {},
  };

  constructor(props) {
    super(props);
    this.state = {
      itemList: [],
      selected: null,
      saving: false,
      // `true` = don't seed from the URL query: this is a picker inside a popup, not a list page.
      searchItem: new ChecklistCopySourceSearchParams(true),
    };

    ChecklistCopySourceItem.clickOnSelect = this.clickOnSelect.bind(this);
    this.loadCopySources = this.loadCopySources.bind(this);
  }

  clickOnSelect(item) {
    const templist = [...this.state.itemList];
    templist.forEach((element) => {
      element.Selected = element.OpportunityId === item.OpportunityId ? !element.Selected : false;
    });
    this.setState({
      itemList: templist,
      selected: templist.find((element) => element.Selected) ? { ...item } : null,
    });
  }

  // TabOfDataTable calls this with (orderById, orderDir, pageNumber, numberOfElements, searchItem, clearCache).
  // The list is short and already scoped to this Opportunity's client, so there is nothing to page through and
  // the search filters the loaded rows here rather than round-tripping. An error object is passed straight
  // through - the table reports it.
  loadCopySources(orderById, orderDir, pageNumber, numberOfElements, searchItem) {
    return OpportunitySecurityChecklistService.GetCopySources(this.props.opportunityId).then((sources) =>
      Array.isArray(sources) ? ChooseChecklistSourcePanel.filterSources(sources, searchItem) : sources
    );
  }

  // Matches the typed text against the Opportunity's id and name. TabOfDataTable URL-encodes the search item's
  // strings before handing them over, so decode before comparing.
  static filterSources(sources, searchItem) {
    const filter = decodeURIComponent(
      searchItem?.SingleSearchLineText ? searchItem.SingleSearchLineText : searchItem?.OpportunityIdOrName ?? ''
    )
      .trim()
      .toLowerCase();

    if (filter === '') {
      return sources;
    }

    return sources.filter(
      (source) =>
        String(source.opportunityId).toLowerCase().includes(filter) ||
        (source.opportunityName ?? '').toLowerCase().includes(filter)
    );
  }

  // Reads the chosen Opportunity's checklist and hands it over with the row it came from - the caller needs both
  // (the checklist to pre-fill with, the row to name the source in the acknowledgement and the audit comment).
  handleOnSubmit() {
    const selected = this.state.selected;
    this.setState({ saving: true });

    OpportunitySecurityChecklistService.GetById(selected.OpportunityId)
      .then((checklist) => {
        if (checklist?.error) {
          ErrorMessage.SetErrorMessage(checklist.error);
          this.setState({ saving: false });
          return;
        }
        this.props.clickOnSubmit(checklist, selected);
      })
      .catch(() => {
        ErrorMessage.SetErrorMessage('Error! ErrorCode: CCSP01. The Security Checklist could not be read!');
        this.setState({ saving: false });
      });
  }

  // In flow directly under the list - NOT ButtonFooterContainer, whose .buttonfooter is
  // `position:absolute; bottom:20px` and would pin the button to the bottom of the (near-fullscreen)
  // popup, leaving a dead band under the list and overlapping its last rows.
  renderSubmitButton() {
    return (
      <div className='d-flex justify-content-center py-3'>
        <FigmaButton
          label='Copy Checklist'
          icon={`fa-solid fa-copy${this.state.saving ? ' fa-spin' : ''}`}
          disabled={this.state.saving || !this.state.itemList.find((item) => item.Selected)}
          type={ButtonTypeEnum.ExtraLargeNormal}
          color={ButtonColorsEnum.Primary}
          iconPosition={IconPositionEnum.Left}
          onClick={() => this.handleOnSubmit()}
        />
      </div>
    );
  }

  render() {
    return (
      <OverWindow
        title={'Choose Opportunity To Copy The Security Checklist From'}
        clickOnCancel={() => {
          if (!this.state.saving) this.props.clickOnCancel(false);
        }}
        closeOnClickOutside={false}
        iconName='fa-solid fa-copy'
        zIndex={1050}
        // ow-fit: size the window to its content (capped at the viewport). Plain .top-of-boxFigma is a
        // fixed near-fullscreen window, which for a picker this compact is mostly empty space.
        className='applySurveyPanel top-of-boxFigma ow-fit'
        verticalScroll={false}
      >
        <div className='container-fluid'>
          {/* .grid-fit-bounds keeps the grid at CONTENT height. Without it TabOfDataTable fits itself to
              the nearest .verticalScroll - which here is the Opportunity detail popup BEHIND this window -
              and runs the list down to the viewport bottom. The cap lets a long list scroll inside the
              grid instead of growing the window, so the action button stays right under the list. */}
          <div
            className='grid-fit-bounds'
            style={{ maxHeight: '60vh' }}
          >
            <TabOfDataTable
              itemList={this.state.itemList}
              type={ChecklistCopySourceItem}
              serviceFunction={this.loadCopySources}
              changeOfItemListFunction={(itemList) => this.setState({ itemList })}
              convertDBItemsFunction={OpportunitySecurityChecklistService.ConvertCopySourcesDBItem}
              showSearchStrip={true}
              singleSearchLinePlaceholder='Search in Opportunity IDs and names...'
              singleSearchLine={true}
              enableUrlPush={false}
              searchParamsType={ChecklistCopySourceSearchParams}
              clickOnStartSearch={(searchItem) => this.setState({ searchItem })}
              searchItem={this.state.searchItem}
              orderBy='OpportunityId'
              showPagination={false}
              isFilterDisabled={true}
              isRefreshDisabled={true}
              itemTypeName='Opportunities with a completed checklist'
              errorCode='CCSP02'
            />
          </div>
          {this.renderSubmitButton()}
        </div>
      </OverWindow>
    );
  }
}
